import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Plus, Pill, Calendar, AlertCircle, Bell, BellOff, Clock, Package, AlertTriangle, Phone } from 'lucide-react';
import { toast } from 'sonner';
import API from '@/utils/api';
import AddMedicationDialog from './AddMedicationDialog';
import MedicationReminderDialog from './MedicationReminderDialog';
import RefillTrackingDialog from './RefillTrackingDialog';
import {
  calculateSupplyRemaining,
  formatSupplyStatus,
  getSupplyStatusColor,
} from '@/services/medicationRefillService';

const MedicationsTab = () => {
  const [medications, setMedications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [reminderMed, setReminderMed] = useState(null); // Medication being edited for reminders
  const [refillMed, setRefillMed] = useState(null); // Medication being edited for refill tracking

  useEffect(() => {
    fetchMedications();
  }, []);

  const fetchMedications = async () => {
    try {
      const response = await API.get('/medical/medications');
      setMedications(response.data);
    } catch (error) {
      console.error('Failed to fetch medications:', error);
      // Calm error message
      toast.error("Couldn't load medications", {
        description: 'Please try refreshing the page'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (medData) => {
    try {
      await API.post('/medical/medications', medData);
      toast.success('Medication added');
      fetchMedications();
      setShowAdd(false);
    } catch (error) {
      toast.error("Couldn't save medication", {
        description: 'Please try again'
      });
    }
  };

  const handleDelete = async (id) => {
    try {
      await API.delete(`/medical/medications/${id}`);
      toast.success('Medication removed');
      fetchMedications();
    } catch (error) {
      toast.error("Couldn't remove medication", {
        description: 'Please try again'
      });
    }
  };

  const handleReminderSave = async (updatedMed) => {
    try {
      await API.put(`/medical/medications/${updatedMed.id}`, {
        reminders_enabled: updatedMed.reminders_enabled,
        reminder_times: updatedMed.reminder_times,
      });
      fetchMedications();
    } catch (error) {
      console.error('Failed to save reminder settings:', error);
      throw error;
    }
  };

  const handleRefillSave = async (updatedMed) => {
    try {
      await API.put(`/medical/medications/${updatedMed.id}`, {
        refill_tracking_enabled: updatedMed.refill_tracking_enabled,
        quantity_prescribed: updatedMed.quantity_prescribed,
        quantity_remaining: updatedMed.quantity_remaining,
        doses_per_day: updatedMed.doses_per_day,
        days_supply: updatedMed.days_supply,
        refills_remaining: updatedMed.refills_remaining,
        refill_alert_days: updatedMed.refill_alert_days,
        pharmacy_phone: updatedMed.pharmacy_phone,
        last_refill_date: updatedMed.last_refill_date,
      });
      fetchMedications();
    } catch (error) {
      console.error('Failed to save refill settings:', error);
      throw error;
    }
  };

  // Get supply status for a medication
  const getSupplyStatus = (med) => {
    if (!med.refill_tracking_enabled) return null;
    return calculateSupplyRemaining(med);
  };

  // Format reminder times for display
  const formatReminderTimes = (times) => {
    if (!times || times.length === 0) return '';
    return times.map(t => {
      const [hours, minutes] = t.split(':');
      const h = parseInt(hours, 10);
      const ampm = h >= 12 ? 'PM' : 'AM';
      const hour12 = h % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }).join(', ');
  };

  if (loading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-32 w-full" />
        ))}
      </div>
    );
  }

  if (medications.length === 0) {
    return (
      <>
        <div className="text-center py-12">
          <div className="mx-auto w-16 h-16 bg-[#B8E6D5] rounded-full flex items-center justify-center mb-4">
            <Pill className="h-8 w-8 text-gray-700" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Track Your Medications</h3>
          <p className="text-gray-500 mb-4">Keep a record of what you&apos;re taking for better health management</p>
          <Button onClick={() => setShowAdd(true)} className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
            <Plus className="h-4 w-4 mr-2" />
            Add Your First Medication
          </Button>
        </div>
        <AddMedicationDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      </>
    );
  }

  return (
    <>
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Medications</h3>
        <Button onClick={() => setShowAdd(true)} size="sm" className="bg-[#B8E6D5] hover:bg-[#A0D6C5] text-gray-900">
          <Plus className="h-4 w-4 mr-2" />
          Add Medication
        </Button>
      </div>

      <div className="space-y-4">
        {medications.map((med, index) => (
          <div
            key={med.id}
            className="border border-gray-200 rounded-lg p-5 bg-white hover:shadow-md transition-shadow"
          >
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-semibold text-gray-900 text-lg">{med.name}</h4>
                <p className="text-sm text-gray-600">{med.dosage} - {med.frequency}</p>
              </div>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                {med.refill_tracking_enabled && (() => {
                  const status = getSupplyStatus(med);
                  if (status?.isLow || status?.isCritical || status?.isEmpty) {
                    return (
                      <Badge className={`flex items-center gap-1 ${
                        status.isEmpty || status.isCritical 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        <AlertTriangle className="h-3 w-3" />
                        {status.isEmpty ? 'Empty' : status.isCritical ? 'Critical' : 'Low'}
                      </Badge>
                    );
                  }
                  return null;
                })()}
                {med.reminders_enabled && med.reminder_times?.length > 0 && (
                  <Badge className="bg-blue-100 text-blue-800 flex items-center gap-1">
                    <Bell className="h-3 w-3" />
                    Reminders
                  </Badge>
                )}
                {!med.end_date && (
                  <Badge className="bg-green-100 text-green-800">Current</Badge>
                )}
              </div>
            </div>

            {/* Refill Status */}
            {med.refill_tracking_enabled && (() => {
              const status = getSupplyStatus(med);
              if (!status || status.error) return null;
              return (
                <div className={`p-3 rounded-lg mb-3 ${getSupplyStatusColor(status)}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      <span className="font-medium text-sm">{formatSupplyStatus(status)}</span>
                    </div>
                    {med.pharmacy_phone && (
                      <a
                        href={`tel:${med.pharmacy_phone}`}
                        className="flex items-center gap-1 text-xs hover:underline"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone className="h-3 w-3" />
                        Call Pharmacy
                      </a>
                    )}
                  </div>
                  {status.percentRemaining != null && (
                    <Progress value={status.percentRemaining} className="h-1.5" />
                  )}
                  {med.refills_remaining != null && (
                    <p className="text-xs mt-1 opacity-80">
                      {med.refills_remaining} refill{med.refills_remaining !== 1 ? 's' : ''} remaining
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Reminder Info */}
            {med.reminders_enabled && med.reminder_times?.length > 0 && (
              <div className="flex items-center gap-2 p-2 bg-blue-50 rounded-lg mb-3 text-sm">
                <Clock className="h-4 w-4 text-blue-600" />
                <span className="text-blue-700">
                  Reminders at {formatReminderTimes(med.reminder_times)}
                </span>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
              <div>
                <span className="text-gray-600">Prescribed for:</span>
                <p className="font-medium text-gray-900">{med.prescribed_for}</p>
              </div>
              <div>
                <span className="text-gray-600">Start date:</span>
                <p className="font-medium text-gray-900">{med.start_date}</p>
              </div>
              {med.end_date && (
                <div>
                  <span className="text-gray-600">End date:</span>
                  <p className="font-medium text-gray-900">{med.end_date}</p>
                </div>
              )}
              {med.effectiveness && (
                <div>
                  <span className="text-gray-600">Effectiveness:</span>
                  <p className="font-medium text-gray-900 capitalize">{med.effectiveness}</p>
                </div>
              )}
            </div>

            {med.allergic_reaction && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg mb-3 flex items-start">
                <AlertCircle className="h-5 w-5 text-red-600 mr-2 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-red-900">Allergic Reaction</p>
                  <p className="text-sm text-red-700">{med.allergic_reaction}</p>
                </div>
              </div>
            )}

            {med.side_effects && (
              <div className="p-3 bg-yellow-50 rounded-lg mb-3">
                <p className="text-sm font-medium text-yellow-900">Side Effects:</p>
                <p className="text-sm text-yellow-700">{med.side_effects}</p>
              </div>
            )}

            {med.notes && (
              <div className="p-3 bg-gray-50 rounded-lg mb-3">
                <p className="text-sm text-gray-700">{med.notes}</p>
              </div>
            )}

            <div className="flex justify-end gap-2 flex-wrap">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setRefillMed(med)}
                className={med.refill_tracking_enabled ? 'text-emerald-600' : 'text-gray-600'}
              >
                <Package className="h-4 w-4 mr-1" />
                {med.refill_tracking_enabled ? 'Track Refills' : 'Set Up Refills'}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setReminderMed(med)}
                className={med.reminders_enabled ? 'text-blue-600' : 'text-gray-600'}
              >
                {med.reminders_enabled ? (
                  <Bell className="h-4 w-4 mr-1" />
                ) : (
                  <BellOff className="h-4 w-4 mr-1" />
                )}
                {med.reminders_enabled ? 'Reminders' : 'Set Reminders'}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(med.id)}
                className="text-red-600 hover:text-red-700"
              >
                Delete
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AddMedicationDialog open={showAdd} onClose={() => setShowAdd(false)} onAdd={handleAdd} />
      
      {/* Medication Reminder Dialog */}
      <MedicationReminderDialog
        isOpen={!!reminderMed}
        onClose={() => setReminderMed(null)}
        medication={reminderMed}
        onSave={handleReminderSave}
      />
      
      {/* Refill Tracking Dialog */}
      <RefillTrackingDialog
        isOpen={!!refillMed}
        onClose={() => setRefillMed(null)}
        medication={refillMed}
        onSave={handleRefillSave}
      />
    </>
  );
};

export default MedicationsTab;